import { PlayerDataSingleton } from "./PlayerDataSingleton";
import { IMedCaseJSON } from "../Types/IMedCaseJSON";
import { AchievementID } from "../Data/Achievements";
import { TExamId } from "../Types/SharedTypes";
import { ICharacterGeneratorSettings } from "./CharacterPortraitGenerator";
import { IHospitalCore } from "../Types/IHospitalCore";
import { GAME_DEBUG_MODE } from "../../engine/Constants/Constants";

export interface IUserData {
    id: string;
    displayName: string;
    // picUrl?: string;
    numOfSolvedCases: number;
    numOfIncorrectDiagnoses: number;
    docScore: number;
    achievements: AchievementID[];
    characterCreatorSettings: ICharacterGeneratorSettings;
}

export enum CoreFailReason {
    NO_ERROR,
    PATIENT_CANT_TALK,
    INVALID_JSON_RESPONSE
}

export interface IScoreBreakdown {
    id?: string,
    score?: number,
    breakdown?: {
        difficultyBonus: number;
        examsXp: number;
        numOfUntreatedSymptoms: number;
        exams: number; // correct ones
        treatments: number; // correct ones
        treatmentsXp: number;
        speedBonus: number;
    },
    success?: boolean,
    error?: string,
}

export class HospitalNetCore {

    // public static async makeRequest<T>(
    //     functionStr: string,
    //     _params: Record<string, string> = {},
    //     isJSON: boolean = true
    // ): Promise<T> {
    //     const authKey = PlayerDataSingleton.getAuthKey();
    //     const isPost = HospitalConfig.postFunctions.indexOf(functionStr) !== -1;
    //
    //     let str = `${HospitalConfig.endpoint}${functionStr}?`;
    //     str += new URLSearchParams(_params).toString();
    //     try {
    //         const fetchParams = {
    //             method: isPost ? "POST" : "GET",
    //             headers: new Headers()
    //         };
    //         if(authKey) {
    //             fetchParams.headers.set(
    //                 "Authorization",
    //                 "Bearer " + authKey
    //             );
    //         }
    //
    //         let _wasError: any = null;
    //         const fetchRes: Response = (
    //             await fetch(
    //                 str,
    //                 fetchParams
    //             ).catch((err) => _wasError = err)
    //                 // .catch((err) => {
    //                 //     throw err;
    //                 // })
    //         );
    //         if(fetchRes.status == 401) {
    //             console.warn("Not authorized!");
    //             return null;
    //         } else if(fetchRes.status == 500) {
    //             console.warn("Unknown 500 error %o", fetchRes);
    //             return null;
    //         } else if (_wasError) {
    //             console.warn("Unknown other error %o", _wasError);
    //             return null;
    //         }
    //         if(isJSON) {
    //             return (await fetchRes.json()) as T;
    //         } else {
    //             return await fetchRes.text() as unknown as T;
    //         }
    //     } catch(err) {
    //         console.warn(err);
    //     }
    //
    //     return null;
    // }

    public static canPatientSpeak(_case: IMedCaseJSON | string): boolean {
        if(typeof _case === "string") {
            const hospitalCore = ENGINE.getWASM<IHospitalCore>("HospitalCore");
            return hospitalCore.canPatientTalk(_case);
        } else {
            const hospitalCore = ENGINE.getWASM<IHospitalCore>("HospitalCore");
            return hospitalCore.canPatientTalk(JSON.stringify(_case));
        }
    }

    public static generateCases(_amt: number):Array<Partial<IMedCaseJSON>> {
        const _cases: Array<Partial<IMedCaseJSON>> = [];
        const hospitalCore = ENGINE.getWASM<IHospitalCore>("HospitalCore");
        for(let i = 0; i < _amt; i++) {
            const _case: IMedCaseJSON =
                JSON.parse(
                    hospitalCore.generateCase(
                        {
                            filters: {
                                departmentFilter: [
                                    0, 1, 2, 3, 4, 5
                                ]
                            }
                        },
                        BigInt(PlayerDataSingleton.getGameTime()),
                    )
                );
            _case.difficultyScore =
                hospitalCore.assessDifficulty(JSON.stringify(_case));
            _cases.push(
                _case
            );
        }
        return _cases;
    }

    public static async getVersion(): Promise<string> {
        const hospitalCore = ENGINE.getWASM<IHospitalCore>("HospitalCore");
        return hospitalCore.getVersion();
    }

    // public static abandonCase(_caseId: string): Promise<boolean> {
    //     return HospitalNetCore.makeRequest<{success?: boolean, error?: string}>("abandonCase", {
    //         caseId: _caseId
    //     }).then((e) => {
    //         if(!e || !e.success || e.error) {
    //             return false;
    //         } else if(e.success) {
    //             return true;
    //         }
    //         return false;
    //     });
    // }

    public static async giveTreatment(
        _caseId: string,
        _treatmentId: string,
    ): Promise<{
        id: string,
        json: IMedCaseJSON,
        error?: string,
    }> {
        const hospitalCore = ENGINE.getWASM<IHospitalCore>("HospitalCore");
        const oldCase = PlayerDataSingleton.getCases().find((e) => e.id === _caseId);
        const newCase = JSON.parse(hospitalCore.giveTreatment(
            JSON.stringify(oldCase),
            BigInt(PlayerDataSingleton.getGameTime()),
            _treatmentId,
        ) || "{}");
        if(!newCase.id) return null;
        return {
            id: newCase.id,
            json: newCase
        };
    }

    public static calculateRewardScore(
        _case: IMedCaseJSON,
        _isSuccess: boolean
    ): IScoreBreakdown {
        const hospitalCore = ENGINE.getWASM<IHospitalCore>("HospitalCore");
        if(!_case) throw new Error("No case provided");
        if(!hospitalCore) throw new Error("No hospital core available");
        return JSON.parse(hospitalCore.calculateRewardScore(
            JSON.stringify(_case),
            BigInt(PlayerDataSingleton.getGameTime()),
            _isSuccess,
        ));
    }

    public static async makeDiagnosis(
        _case: IMedCaseJSON,
        _diagnosisId: string,
    ): Promise<IScoreBreakdown> {
        const hospitalCore = ENGINE.getWASM<IHospitalCore>("HospitalCore");
        if(!_case) throw new Error("No case provided");
        if(!hospitalCore) throw new Error("No hospital core available");
        // @ts-ignore
        // const correct = _case["diagnosisId"] === _diagnosisId;
        const correct = hospitalCore.makeDiagnosis(
            JSON.stringify(_case),
            _diagnosisId
        );
        const breakdown = hospitalCore.calculateRewardScore(
            JSON.stringify(_case),
            BigInt(PlayerDataSingleton.getGameTime()),
            correct,
        );
        if(breakdown) {
            const breakdownObj: {
                score: number;
                breakdown: {
                    difficultyBonus: number;
                    examsXp: number;
                    numOfUntreatedSymptoms: number;
                    exams: number; // correct ones
                    treatments: number; // correct ones
                    treatmentsXp: number;
                    speedBonus: number;
                }
            } = JSON.parse(breakdown);
            const retVal: IScoreBreakdown = {
                breakdown: breakdownObj.breakdown,
                id: _case.id,
                score: breakdownObj.score,
                success: correct,
            };
            return retVal;
        } else {
            throw new Error("Breakdown failed to generate");
        }
    }

    public static async acknowledgeExam(_caseId: string, _examId: string): Promise<{
        id: string;
        json: IMedCaseJSON;
        error?: unknown;
    }> {
        const hospitalCore = ENGINE.getWASM<IHospitalCore>("HospitalCore");
        const oldCase = PlayerDataSingleton.getCases().find((e) => e.id === _caseId);
        if(GAME_DEBUG_MODE)
            console.log(JSON.parse(JSON.stringify(oldCase)));
        const newCase = JSON.parse(hospitalCore.acknowledgeExamination(
            JSON.stringify(oldCase),
            BigInt(PlayerDataSingleton.getGameTime()),
            _examId,
        ) || "{}");
        if(!newCase.id) return {
            id: "",
            json: null,
            error: "Error acknowledging exam"
        };
        if(GAME_DEBUG_MODE)
            console.log(JSON.parse(JSON.stringify(newCase)));
        return {
            id: newCase.id,
            json: newCase
        };
    }

    public static async acknowledgeTreatment(_caseId: string, _treatmentId: string): Promise<{
        id: string;
        json: IMedCaseJSON;
        error?: unknown;
    }> {
        const hospitalCore = ENGINE.getWASM<IHospitalCore>("HospitalCore");
        const oldCase = PlayerDataSingleton.getCases().find((e) => e.id === _caseId);
        const newCase = JSON.parse(hospitalCore.acknowledgeTreatment(
            JSON.stringify(oldCase),
            BigInt(PlayerDataSingleton.getGameTime()),
            _treatmentId,
        ) || "{}");
        if(!newCase.id) return {
            id: "",
            json: null,
            error: "Error acknowledging trt"
        };
        return {
            id: newCase.id,
            json: newCase
        };
    }

    public static async requestExam(caseId: string, examId: string): Promise<{
        id: string; json: IMedCaseJSON, reason?: CoreFailReason;
    }> {
        // try {
        //     const result = await HospitalNetCore.makeRequest<TReqResult>(
        //         "addExamination",
        //         {
        //             caseId: caseId,
        //             examId: examId
        //         }
        //     ).then((e) => {
        //         // console.log(e);
        //         if(!e.error) {
        //             return {id: e.id, json: e.json as IMedCaseJSON};
        //         }
        //         console.error(e.error);
        //         return null;
        //     });
        //     return result;
        // } catch(err) {
        //     console.error(err);
        //     return null;
        // }
        const hospitalCore = ENGINE.getWASM<IHospitalCore>("HospitalCore");
        const oldCase = PlayerDataSingleton.getCases().find((e) => e.id === caseId);
        const oldCaseStr = JSON.stringify(oldCase);

        if(
            hospitalCore.isSpeechRequired(examId) &&
            !HospitalNetCore.canPatientSpeak(oldCaseStr)
        ) {
            return {
                id: null,
                json: null,
                reason: CoreFailReason.PATIENT_CANT_TALK
            };
        }
        const newCase = JSON.parse(hospitalCore.addExamination(
            oldCaseStr,
            BigInt(PlayerDataSingleton.getGameTime()),
            examId,
        ) || "{}");

        if(!newCase.id) return {
            id: null,
            json: null,
            reason: CoreFailReason.INVALID_JSON_RESPONSE
        };
        return {
            id: newCase.id,
            json: newCase
        };
    }

    public static async getCase(caseId: string): Promise<{id: string; json: IMedCaseJSON}> {
        return {
            id: caseId,
            json: PlayerDataSingleton.getCases().find((e) => e.id === caseId)
        };
    }

    public static async getUserData(): Promise<IUserData> {
        return PlayerDataSingleton.getUserData();
    }
}
